import { faker } from '@faker-js/faker';

import { coerceBoolean, random } from '@hsp/utilities';

import donorComposite from 'assets/data/fake/donor-composite.json';
import mailByState from 'assets/data/fake/mail-by-state.json';
import mailInsights from 'assets/data/fake/mail-insight.json';
import topClientMailing from 'assets/data/fake/top-mailing-client.json';
import { defaultMockTime } from 'constants/index';
import { Uri } from 'constants/uri';
import { IResult } from 'types/api/common';
import { IDonorComposite, IMailByState, IMailInsights, IMailPerformance, ITopClientMailing } from 'types/api/dashboard';

import { BaseHttpService } from './http/base-http.service';

class DashboardService extends BaseHttpService {
  constructor() {
    super();
  }

  getDonorComposite(): Promise<IResult<IDonorComposite[]>> | undefined {
    return this.get({ url: Uri.GetDonorComposite });
  }

  getMailByState(): Promise<IResult<IMailByState[]>> | undefined {
    return this.get({ url: Uri.GetMailByState });
  }

  getMailInsights(): Promise<IResult<IMailInsights[]>> | undefined {
    return this.get({ url: Uri.GetMailInsights });
  }

  getMailPerformance(): Promise<IResult<IMailPerformance[]>> | undefined {
    return this.get({ url: Uri.GetMailPerformance });
  }

  getTopClientMailing(): Promise<IResult<ITopClientMailing[]>> | undefined {
    return this.get({ url: Uri.GetTop5ClientMailing });
  }
}

export const dashboardService = new DashboardService();

if (coerceBoolean(process.env.REACT_APP_API_DASHBOARD_FAKE)) {
  const mocked = dashboardService.mock(defaultMockTime);
  const mock = mocked?.adapter;

  mock?.onGet(Uri.GetDonorComposite).reply((): Promise<[number, IResult<IDonorComposite[]>]> => {
    return new Promise((resolve) => {
      const result: IResult<IDonorComposite[]> = {
        dataContainer: donorComposite,
        failure: false,
        statusCode: 200,
        success: true,
      };

      resolve([200, result]);
    });
  });

  mock?.onGet(Uri.GetMailByState).reply((): Promise<[number, IResult<IMailByState[]>]> => {
    return new Promise((resolve) => {
      const result: IResult<IMailByState[]> = {
        dataContainer: mailByState,
        failure: false,
        statusCode: 200,
        success: true,
      };

      resolve([200, result]);
    });
  });

  mock?.onGet(Uri.GetMailInsights).reply((): Promise<[number, IResult<IMailInsights[]>]> => {
    return new Promise((resolve) => {
      const result: IResult<IMailInsights[]> = {
        dataContainer: mailInsights,
        failure: false,
        statusCode: 200,
        success: true,
      };

      resolve([200, result]);
    });
  });

  mock?.onGet(Uri.GetMailPerformance).reply((): Promise<[number, IResult<IMailPerformance[]>]> => {
    return new Promise((resolve) => {
      const data: IMailPerformance[] = [];
      const length = random(100);

      const caged = faker.date.between({ from: '2020-01-01T00:00:00.000Z', to: new Date() });
      const mailed = faker.date.future({ refDate: caged, years: 1 });

      Array.from({ length }).forEach(() => {
        const record = {
          Average: faker.number.int({ max: 100 }),
          CLM: faker.number.int({ max: 10 }),
          Cost: faker.number.int({ max: 9999 }),
          Dates: {
            Caged: caged,
            Mailed: mailed,
          },
          Donors: faker.number.int({ max: 9999 }),
          Gross: faker.number.int({ max: 9999 }),
          Net: faker.number.int({ max: 9999 }),
          NLM: faker.number.int({ max: 10 }),
          NonDonors: faker.number.int({ max: 9999 }),
          PackageId: faker.number.int({ max: 9999 }),
          PackageName: `${faker.string.numeric({ length: 4 }).toUpperCase()}ph${faker.string.numeric({
            allowLeadingZeros: true,
            length: 2,
          })}`,
          Quantity: faker.number.int({ max: 9999 }),
        };

        data.push(record);
      });

      const result: IResult<IMailPerformance[]> = {
        dataContainer: data as unknown as IMailPerformance[],
        failure: false,
        statusCode: 200,
        success: true,
      };

      resolve([200, result]);
    });
  });

  mock?.onGet(Uri.GetTop5ClientMailing).reply((): Promise<[number, IResult<ITopClientMailing[]>]> => {
    return new Promise((resolve) => {
      const result: IResult<ITopClientMailing[]> = {
        dataContainer: topClientMailing,
        failure: false,
        statusCode: 200,
        success: true,
      };

      resolve([200, result]);
    });
  });
}
